export function parseAcademyDateString(s: string): Date | null {
	if (!s) return null;

	// handle ISO 8601 directly
	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
		return new Date(s);
	}

	// expects "DD-MM-YYYY HH:mm:ss"
	const m = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s);
	if (!m) return null;
	const [, dd, MM, yyyy, HH, mm, ss = '00'] = m;
	return new Date(`${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}`);
}

export function parseDMYDateTime(input: string): Date {
	if (!input) return new Date(NaN);

	// handle ISO 8601
	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(input)) {
		return new Date(input);
	}

	const [datePart, timePart = '00:00:00'] = input.trim().split(' ');
	const [dd, mm, yyyy] = datePart.split('-').map(Number);
	const [HH = 0, MM = 0, SS = 0] = timePart.split(':').map(Number);

	if (!yyyy || !mm || !dd) return new Date(NaN);

	return new Date(yyyy, mm - 1, dd, HH, MM, SS, 0);
}

export function formatAcademyDateString(input: string | Date, locale?: string): string {
	// Normalize input
	const date = input instanceof Date ? input : parseAcademyDateString(input);

	if (!(date instanceof Date) || isNaN(date.getTime())) return '';

	// If locale is provided, format with localized month name only
	if (locale) {
		const formatted = date.toLocaleDateString(locale === 'en' ? 'en-GB' : locale, {
			day: '2-digit',
			month: 'long',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
		// Replace any text between year and time (like "at", "à", "um", etc.) with comma
		return formatted.replace(/(\d{4})\s+\S+\s+(\d{2}:\d{2})/, '$1, $2');
	}

	return date
		.toLocaleString('en-GB', {
			day: '2-digit',
			month: 'long',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZoneName: 'short',
		})
		.replace(/ ([A-Z]+)$/, ' ($1)')
		.replace(' at ', ', ');
}

interface FormatDateProps {
	dateInput: string | Date;
	locale: string;
}

export const formatDate = ({ dateInput, locale }: FormatDateProps): string => {
	const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

	if (isNaN(date.getTime())) {
		throw new RangeError(`Invalid date input: ${dateInput}`);
	}

	return new Intl.DateTimeFormat(locale, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	}).format(date);
};

export const formatDateNumeric = (dateInput: string | Date): string => {
	const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

	if (isNaN(date.getTime())) {
		throw new RangeError(`Invalid date input: ${dateInput}`);
	}

	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');

	return `${day}.${month}.${date.getFullYear()}`;
};

export const getLocalizedTime = (isoString: string, locale: string = 'en'): string => {
	const date = new Date(isoString);

	const formatter = new Intl.DateTimeFormat(locale, {
		hourCycle: 'h23',
		hour: '2-digit',
		minute: '2-digit',
	});

	return formatter.format(date);
};

export const dateDifferenceInDays = (a: Date, b: Date): string => {
	const msInDay = 1000 * 60 * 60 * 24;
	const diffDays = Math.abs(a.getTime() - b.getTime()) / msInDay;

	return diffDays > 350 ? '365+' : Math.floor(diffDays).toString();
};

export const formatDuration = (timeString: string | undefined | null): string => {
	if (!timeString) return '';
	const parts = timeString.split(':');
	if (parts.length !== 3) return timeString;
	const [hh, mm, ss] = parts;
	if (parseInt(hh, 10) === 0 && parseInt(mm, 10) === 0 && parseInt(ss, 10) === 0) return '';
	return parseInt(hh, 10) === 0 ? `${mm}:${ss}` : timeString;
};
